import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * What this user may do at the counter.
 *
 * Presentation only. The server authorises every command against the live catalogue, so a visible
 * button is not an open door and a hidden one is not a closed one. Two of these are worth reading
 * twice, because both are held by *nobody* who also does the work the screen is for:
 *
 *   - `teller.vault.view` is not held by the shipped Teller role. A teller can float a till from a
 *     vault they cannot see the balance of.
 *   - `savings.product.view` is not held by it either, which is why the withdrawal rail cannot show
 *     the withdrawal fee or the product's minimum balance — the two figures the server checks a
 *     withdrawal against.
 *
 * And one that is barred rather than merely absent: `teller.session.open` and
 * `teller.eod.submit.approve` are declared an incompatible pair in the segregation matrix, so
 * nobody who can open a session can ever countersign its close. The close dialog states that as a
 * fact rather than offering the usual "you may hold both halves" hedge.
 */
export const TELLER_PERMISSIONS = {
  tillView: PERMISSIONS.teller.tillView,
  vaultView: PERMISSIONS.teller.vaultView,
  sessionOpen: PERMISSIONS.teller.sessionOpen,
  sessionClose: PERMISSIONS.teller.sessionClose,
  eodSubmit: PERMISSIONS.teller.eodSubmit,
  eodSubmitApprove: PERMISSIONS.teller.eodSubmitApprove,
  denominationRecord: PERMISSIONS.teller.denominationRecord,
  vaultTransfer: PERMISSIONS.teller.vaultTransfer,
  vaultTransferApprove: PERMISSIONS.teller.vaultTransferApprove,
  fieldTillAssign: PERMISSIONS.teller.fieldTillAssign,
  depositPost: PERMISSIONS.teller.depositPost,
  withdrawalPost: PERMISSIONS.teller.withdrawalPost,
  repaymentPost: PERMISSIONS.teller.repaymentPost,
  reconcile: PERMISSIONS.teller.reconcile,
  /** Creating a till or a vault is branch infrastructure, and carries an `admin.*` code by design. */
  infrastructureManage: PERMISSIONS.admin.branchManage,
  infrastructureManageApprove: PERMISSIONS.admin.branchManageApprove,

  savingsView: PERMISSIONS.savings.view,
  savingsDepositPost: PERMISSIONS.savings.depositPost,
  savingsWithdrawalPost: PERMISSIONS.savings.withdrawalPost,
  savingsWithdrawalPostApprove: PERMISSIONS.savings.withdrawalPostApprove,
  securityDepositTopUp: PERMISSIONS.savings.securityDepositTopUp,
  savingsProductView: PERMISSIONS.savings.productView,

  loansView: PERMISSIONS.loans.view,
  loansRepaymentPost: PERMISSIONS.loans.repaymentPost,

  userView: PERMISSIONS.admin.userView,
  auditView: PERMISSIONS.ops.auditView,
} as const

export interface TellerPermissions {
  canViewTills: boolean
  canViewVaults: boolean
  canOpenSession: boolean
  canRecordCount: boolean
  canSubmitEod: boolean
  canApproveEod: boolean
  canMoveVaultCash: boolean
  canApproveVaultTransfer: boolean
  canAssignFieldTill: boolean
  canManageInfrastructure: boolean
  canApproveInfrastructure: boolean
  /** Both halves are required at the counter: the savings rail and the drawer's custody check. */
  canPostDeposit: boolean
  canPostWithdrawal: boolean
  /** Held by nobody who also pays out, in practice — but asserted, never assumed. */
  canApproveWithdrawal: boolean
  canTopUpSecurityDeposit: boolean
  canPostRepayment: boolean
  canViewSavings: boolean
  canViewSavingsProducts: boolean
  canViewLoans: boolean
  canViewStaff: boolean
}

export function useTellerPermissions(): TellerPermissions {
  const { user } = useAuth()
  const permissions = user?.permissions

  return useMemo(() => {
    const holds = (code: string) => permissions?.some((held) => held === code) ?? false

    return {
      canViewTills: holds(TELLER_PERMISSIONS.tillView),
      canViewVaults: holds(TELLER_PERMISSIONS.vaultView),
      canOpenSession: holds(TELLER_PERMISSIONS.sessionOpen),
      canRecordCount: holds(TELLER_PERMISSIONS.denominationRecord),
      canSubmitEod: holds(TELLER_PERMISSIONS.eodSubmit),
      canApproveEod: holds(TELLER_PERMISSIONS.eodSubmitApprove),
      canMoveVaultCash: holds(TELLER_PERMISSIONS.vaultTransfer),
      canApproveVaultTransfer: holds(TELLER_PERMISSIONS.vaultTransferApprove),
      canAssignFieldTill: holds(TELLER_PERMISSIONS.fieldTillAssign),
      canManageInfrastructure: holds(TELLER_PERMISSIONS.infrastructureManage),
      canApproveInfrastructure: holds(TELLER_PERMISSIONS.infrastructureManageApprove),

      // The counter permission and the module permission are both required, and they are checked
      // in different places: `savings.deposit.post` by the savings module, `teller.deposit.post` by
      // the cash drawer once it knows the drawer is a counter drawer. A user holding one and not
      // the other gets a refusal from whichever runs first, so the button needs both.
      canPostDeposit:
        holds(TELLER_PERMISSIONS.savingsDepositPost) && holds(TELLER_PERMISSIONS.depositPost),
      canPostWithdrawal:
        holds(TELLER_PERMISSIONS.savingsWithdrawalPost) && holds(TELLER_PERMISSIONS.withdrawalPost),
      canApproveWithdrawal: holds(TELLER_PERMISSIONS.savingsWithdrawalPostApprove),
      canTopUpSecurityDeposit:
        holds(TELLER_PERMISSIONS.securityDepositTopUp) && holds(TELLER_PERMISSIONS.depositPost),

      // The drawer's half of a collection is `teller.deposit.post`, not `teller.repayment.post`.
      // A repayment reaches the drawer as an ordinary cash-in, and `CashDrawer.EnsurePermitted`
      // asks for `teller.deposit.post` on every `CashDirection.In` leg whatever caused it. The
      // catalogue does declare `teller.repayment.post` and the shipped Teller role holds it, but no
      // endpoint in any module consumes it — gating the rail on it would enable the button for a
      // role the drawer then refuses with `auth.permission_denied`, which arrives with the money
      // already on the counter.
      canPostRepayment:
        holds(TELLER_PERMISSIONS.loansRepaymentPost) && holds(TELLER_PERMISSIONS.depositPost),

      canViewSavings: holds(TELLER_PERMISSIONS.savingsView),
      canViewSavingsProducts: holds(TELLER_PERMISSIONS.savingsProductView),
      canViewLoans: holds(TELLER_PERMISSIONS.loansView),
      canViewStaff: holds(TELLER_PERMISSIONS.userView),
    }
  }, [permissions])
}
